import {
  Component,
  Inject,
  ElementRef,
  ViewChild,
  signal,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { UserPlan, UserSettings } from '@app/auth/user.model';

interface LocationOption {
  id?: number;
  city: string;
  state: string;
  zip?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

@Component({
  selector: 'app-location-filter-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './location-filter-dialog.component.html',
  styleUrl: './location-filter-dialog.component.scss',
})
export class LocationFilterDialogComponent implements OnInit {
  form: FormGroup;

  options: LocationOption[] = [];
  filteredOptions: LocationOption[] = [];

  selectedLocation: LocationOption | null = null;

  loading = signal(false);
  minSearchLength = 5;

  defaultSearchRadius = 250;
  minSearchRadius = 50;
  maxSearchRadius = 750;

  userPlan: UserPlan | null = null;
  userSettings: UserSettings | null = null;

  @ViewChild('locationInput') locationInput!: ElementRef<HTMLInputElement>;
  @ViewChild('locationInput', { read: MatAutocompleteTrigger })
  locationTrigger!: MatAutocompleteTrigger;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      label: string;
      value: string;
      latitude?: number | string | null;
      longitude?: number | string | null;
      radius?: number | null;
    },
    private dialogRef: MatDialogRef<LocationFilterDialogComponent>,
    private fb: FormBuilder,
    private appService: AppService,
    private authService: AuthService
  ) {
    this.userPlan = this.authService.getUserPlanFromStorage();
    this.userSettings = this.authService.getUserSettingsFromStorage();

    this.defaultSearchRadius =
      this.userSettings?.default_search_radius_miles ?? 250;

    this.maxSearchRadius =
      this.userPlan?.max_search_radius_miles ?? 750;

    this.form = this.fb.group({
      value: [this.data.value ?? ''],
      radius: [
        this.data.radius ?? this.defaultSearchRadius,
        [
          Validators.required,
          Validators.min(this.minSearchRadius),
          Validators.max(this.maxSearchRadius),
        ],
      ],
    });
  }

  ngOnInit(): void {
    if (!this.data.value) {
      return;
    }

    this.selectedLocation = this.parseLocationFromValue(this.data.value);
  }

  filterLocation(): void {
    this.selectedLocation = null;

    const value = this.locationInput.nativeElement.value.toLowerCase();

    if (value.length < this.minSearchLength) {
      this.filteredOptions = [];
      return;
    }

    this.updateOptions(value);
  }

  updateOptions(search: string): void {
    this.loading.set(true);

    this.appService.getLocations(search).subscribe({
      next: (res) => {
        const data = Array.isArray(res.data) ? res.data : [];

        this.options = data;
        this.filteredOptions = data;

        setTimeout(() => this.loading.set(false), 500);
        setTimeout(() => this.locationTrigger.openPanel());
      },
      error: () => {
        this.options = [];
        this.filteredOptions = [];

        setTimeout(() => this.loading.set(false), 500);
      },
    });
  }

  formatLocation(item: LocationOption | null): string {
    if (!item) return '';

    return `${item.city}, ${item.state}${item.zip ? ` ${item.zip}` : ''}`;
  }

  displayLocation(item: LocationOption | string | null): string {
    if (!item) return '';

    if (typeof item === 'string') {
      return item;
    }

    return this.formatLocation(item);
  }

  onLocationSelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value as LocationOption;

    this.selectedLocation = item;

    this.form.patchValue({
      value: item,
    });

    this.form.get('value')?.setErrors(null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.selectedLocation) {
      this.form.get('value')?.setErrors({ invalidLocation: true });
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      location: this.formatLocation(this.selectedLocation),
      latitude: this.selectedLocation.latitude,
      longitude: this.selectedLocation.longitude,
      radius: this.form.get('radius')?.value,
    });
  }

  clear(): void {
    this.dialogRef.close(null);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private parseLocationFromValue(value: string): LocationOption {
    const [cityPart, rest] = value.split(',');
    const restParts = rest?.trim().split(' ') ?? [];

    return {
      city: cityPart?.trim() ?? value,
      state: restParts[0] ?? '',
      zip: restParts[1] ?? null,
      latitude: this.data.latitude ?? null,
      longitude: this.data.longitude ?? null,
    };
  }
}